/**
 * quote-rental — 견적 모달의 **임대 전용** 파생 계산.
 *
 * ★ 왜 `quote-math.ts` 에 넣지 않는가 ─────────────────────────────────
 *   그 파일은 판매 견적의 정본이고 동결 테스트(quote-math.test.ts)가 걸려
 *   있다. 임대는 단가를 만드는 방식이 통째로 다르다 — 판매는
 *   `원가 ÷ (1 − 마진율)` 로 **개당 단가**를 만들고, 임대는 원가를 기간·수량으로
 *   나눠 **월 단가**를 만든다. 한 파일에서 분기시키면 판매 쪽 함수마다
 *   `if (임대)` 가 붙어 두 계산이 서로를 오염시킨다. 그래서 새 파일로 격리하고,
 *   판매 쪽에서 이미 검증된 것(원가 유도)만 **호출해서 재사용**한다.
 *
 * ★ 계산 자체는 여기 없다 ────────────────────────────────────────────
 *   단가·원가·마진은 전부 `@hcltech/shared` 의 `computeRentalPricing` 이 한다.
 *   이 파일이 하는 일은 **입력을 모으는 것**뿐이다:
 *     ① 이 라인의 매입금액 B 는 얼마인가 (판매 쪽 원가 유도를 그대로 씀)
 *     ② 이 라인이 쓸 프리셋은 무엇인가 (사람이 고른 것 / default_for 초기값)
 *     ③ 프리셋의 항목·정책을 계산 함수의 인자 모양으로 바꾼다
 *   설정 화면의 '계산 미리보기'와 **같은 함수**를 부르므로 값이 갈라질 수 없다.
 */

import {
  computeRentalPricing,
  type RentalPricing,
} from '@hcltech/shared/rental/rental-pricing';
import {
  toRentalPolicy, toRentalCostItems,
} from '@hcltech/shared/schemas/rental-preset.schema';
import type { RentalPreset } from '../api/rental-preset-api';
import {
  parseInt0, serverRowCostKrw, isServerChild, anyCostEntered,
  type QuoteRowLike,
} from './quote-math';

// ─── 입력 타입 ───────────────────────────────────────────────────────

/**
 * 임대 입력을 가진 행. `QuoteModalRow` 의 부분집합이라 모달이 그대로 넘긴다.
 * 전부 문자열인 이유는 입력 중인 값을 그대로 들고 있어야 커서가 안 튀기 때문.
 */
export interface RentalRowLike extends QuoteRowLike {
  /**
   * 차단 사유 문구에 쓸 품목 이름. 계산에는 **쓰지 않는다** —
   * "'GPU 서버' 라인의 기간을 입력하세요" 처럼 어느 줄인지 짚어 주기 위한 것뿐이다.
   * `QuoteRowLike` 에는 없어서(합계 계산에 필요 없으므로) 여기서 더한다.
   */
  option_part:         string | null;
  description:         string | null;
  /** 서버 헤더행의 신품/중고. 프리셋 초기 선택에만 쓴다. */
  serverCondition:     'NEW' | 'USED' | null;
  /** 임대기간 N (개월). 시트 E열. */
  rentalTermMonths:    string;
  /** 고른 프리셋 id. '' 이면 미선택. */
  rentalPresetId:      string;
  /** 수동 임대단가 (시트 R열). '' 이면 자동 계산. */
  rentalManualUnitKrw: string;
}

/** 한 라인의 임대 계산 결과. */
export interface RentalLineResult {
  /** 매입금액합계 B — 수량 반영 후(시트 A35). */
  purchaseKrw: number;
  preset:      RentalPreset;
  pricing:     RentalPricing;
}

// ─── ① 매입금액 B ────────────────────────────────────────────────────

/**
 * 이 라인 전체의 매입원가 — 시트 A35.
 *
 *   "매입금액합계는 해당 최상위 상품 line 전체 매입원가입니다.
 *    서버 수량까지 이미 반영한 값을 입력합니다."
 *
 * ★ 새 입력칸을 만들지 않는다. 판매 견적이 이미 같은 값을 유도하고 있다 —
 *   `serverRowCostKrw` 가 서버 헤더행이면 자식 구성품 매입 합계를, 개별 파트면
 *   자기 소스 스냅샷을 준다(개당). 거기에 수량을 곱하면 시트의 B 와 같아진다.
 *   사람이 같은 숫자를 두 번 적게 하면 두 값이 어긋나는 날이 온다.
 *
 * 원가가 확정되지 않으면 null — 호출부가 '원가 확인 필요'로 막아야 한다.
 * 조용히 0 으로 치면 임대총원가가 물류비·인건비만 남고 단가가 폭락한다.
 *
 * ★ '미입력 칸을 0 으로 볼지'는 판매 견적과 **같은 규칙**을 쓴다
 *   (`anyCostEntered` — 매입단가가 하나라도 입력됐으면 나머지 빈 칸은 0).
 *   이걸 false 로 굳혀 두면 `serverRowCostKrw` 가 자식 중 단가 0 인 행 하나만
 *   봐도 null 을 돌려주는데, **기본 구성 품목은 원래 0원**이다(마이그 174 —
 *   섀시 값에 포함). 그래서 섀시에 3천만을 넣어도 서버 라인 원가가 영원히
 *   '확인 필요' 로 남고 단가·계약총액·저장이 통째로 막힌다.
 */
export function rentalLinePurchaseKrw(
  row: RentalRowLike,
  all: readonly QuoteRowLike[],
  fx:  number,
): number | null {
  const qty = parseInt0(row.quantity) ?? 0;
  if (qty <= 0) return null;          // 수량 없이는 임대 계산이 성립하지 않는다
  // ★ 매입원가가 아직 없으면 **0 으로 본다** — 계산을 포기하지 않는다 (2026-08-20).
  //   기획안(adminuxpreview)의 getRentalLinePurchaseTotal 이 그렇게 한다:
  //       return Number.isFinite(total) && total > 0 ? total : 0;
  //   그리고 applyRentalPricingToLine 의 조기 반환 조건은
  //       !term || quantity <= 0 || contractCostKrw == null
  //   셋뿐이라 매입원가는 애초에 중단 사유가 아니다.
  //
  //   그래야 소스를 아직 안 고른 단계에서도 고정비(물류·인건)만으로 임대총원가가
  //   잡히고 단가·월 임대금액·금액 합계가 나온다. 예전처럼 null 을 돌려주면
  //   구성품 하나만 비어도 임대 열 전체가 '-' 가 되어 화면이 죽는다.
  //   매입원가가 '미확정'이라는 사실은 자식 행의 '확인 필요' 가 따로 알린다.
  const unit = serverRowCostKrw(row, all, fx, anyCostEntered(all));
  return (unit == null ? 0 : unit) * qty;
}

// ─── ② 프리셋 고르기 ─────────────────────────────────────────────────

/**
 * 이 라인의 기본 프리셋 — `default_for` 로 **미리 골라 둔다**.
 *
 * ★ 자동 적용이 아니라 초기값이다(마이그 182 헤더). 서안드레: "디폴트로만
 *   지정하고 여러 프리셋 중에 골라서 임대견적 하도록". 드롭다운은 열려 있고
 *   사람이 언제든 바꾼다.
 *
 * 서버 라인만 신품/중고를 안다. 개별 파트는 `default_for` 가 NULL 인 프리셋을
 * 사람이 골라야 하므로 여기서 아무것도 정하지 않는다 — 잘못된 정책이 조용히
 * 걸리는 것보다 비어 있는 편이 낫다.
 */
export function defaultPresetIdFor(
  row:     RentalRowLike,
  presets: readonly RentalPreset[],
): string {
  if (row.lineType !== 'server' || row.serverCondition == null) return '';
  const want = row.serverCondition === 'NEW' ? 'SERVER_NEW' : 'SERVER_USED';
  return presets.find((p) => p.is_active && p.default_for === want)?.id ?? '';
}

/** 라인이 실제로 쓸 프리셋. 고른 것이 없거나 사라졌으면 null. */
export function presetOfRow(
  row:     RentalRowLike,
  presets: readonly RentalPreset[],
): RentalPreset | null {
  const id = row.rentalPresetId || defaultPresetIdFor(row, presets);
  if (!id) return null;
  return presets.find((p) => p.id === id) ?? null;
}

// ─── ③ 라인 계산 ─────────────────────────────────────────────────────

/**
 * 한 라인의 임대 가격. 입력이 덜 찼으면 null(화면이 '입력 필요'로 표시).
 *
 * ★ 서버 구성품(자식행)은 계산하지 않는다. 고객 총액은 최상위 라인만으로
 *   정해진다는 판매 쪽 규칙(CONFIG2-004)이 임대에서도 그대로다 — 구성품까지
 *   더하면 같은 물건이 두 번 계산된다.
 */
export function computeRentalLine(
  row:     RentalRowLike,
  all:     readonly QuoteRowLike[],
  fx:      number,
  presets: readonly RentalPreset[],
): RentalLineResult | null {
  if (isServerChild(row)) return null;

  const preset = presetOfRow(row, presets);
  if (!preset) return null;

  const purchaseKrw = rentalLinePurchaseKrw(row, all, fx);
  if (purchaseKrw == null) return null;

  const months   = parseInt0(row.rentalTermMonths) ?? 0;
  const quantity = parseInt0(row.quantity) ?? 0;
  const manual   = parseInt0(row.rentalManualUnitKrw);

  const pricing = computeRentalPricing(
    toRentalCostItems(preset.items),
    { purchaseKrw, months, quantity },
    toRentalPolicy(preset),
    manual != null && manual > 0 ? manual : null,
  );
  return { purchaseKrw, preset, pricing };
}

// ─── ④ 합계 ──────────────────────────────────────────────────────────

export interface RentalQuoteTotals {
  /** 임대총원가 합 — 시트 O열의 합. */
  costBasisKrw: number;
  /** 계약 총액 합 — 시트 U열의 합. 고객이 내는 금액이다. */
  totalKrw:     number;
  marginKrw:    number;
  /** 전체 마진율(%). 합계가 0이면 null. */
  marginRate:   number | null;
  /** 모든 최상위 라인이 계산됐는가. 하나라도 못 하면 합계는 '확인 필요'. */
  complete:     boolean;
}

export function computeRentalTotals(
  rows:    readonly RentalRowLike[],
  fx:      number,
  presets: readonly RentalPreset[],
): RentalQuoteTotals {
  let costBasisKrw = 0, totalKrw = 0, complete = true;
  for (const row of rows) {
    if (isServerChild(row)) continue;          // 최상위 라인만
    const r = computeRentalLine(row, rows, fx, presets);
    if (r == null || r.pricing.unitKrw <= 0) { complete = false; continue; }
    costBasisKrw += r.pricing.contractCostKrw;
    totalKrw     += r.pricing.totalKrw;
  }
  const marginKrw = totalKrw - costBasisKrw;
  return {
    costBasisKrw, totalKrw, marginKrw,
    marginRate: complete && totalKrw > 0 ? (marginKrw / totalKrw) * 100 : null,
    complete,
  };
}

// ─── ⑤ 저장 차단 사유 ────────────────────────────────────────────────

/**
 * 임대 견적을 저장하면 안 되는 사유. 판매 쪽 `quoteBlockReasons` 와 **별도**다
 * — 임대에서만 필요한 검사이고, 판매 견적에 임대 사유가 섞이면 안 된다.
 *
 * alert 로 튕기지 않고 목록으로 돌려주는 것은 판매 쪽과 같은 이유다:
 * 어느 줄을 고쳐야 하는지 보면서 맞춰야 한다.
 */
export function rentalBlockReasons(
  rows:    readonly RentalRowLike[],
  fx:      number,
  presets: readonly RentalPreset[],
): string[] {
  const out: string[] = [];
  const tops = rows.filter((r) => !isServerChild(r));
  if (tops.length === 0) return out;

  for (const row of tops) {
    const name = row.option_part || row.description || '이름 없는 품목';
    if (!presetOfRow(row, presets)) {
      out.push(`'${name}' 라인의 임대 계산식(프리셋)을 고르세요.`);
      continue;
    }
    if ((parseInt0(row.rentalTermMonths) ?? 0) <= 0) {
      out.push(`'${name}' 라인의 임대 기간(개월)을 입력하세요.`);
      continue;
    }
    if (rentalLinePurchaseKrw(row, rows, fx) == null) {
      out.push(`'${name}' 라인의 매입원가가 확정되지 않았습니다 — 소스를 고르거나 매입단가를 채우세요.`);
      continue;
    }
    const r = computeRentalLine(row, rows, fx, presets);
    if (r == null || r.pricing.unitKrw <= 0) {
      out.push(`'${name}' 라인의 임대단가가 0원입니다 — 기간·수량·마진율을 확인하세요.`);
    }
  }
  return out;
}

// ─── ⑥ 저장 페이로드 ─────────────────────────────────────────────────

/** 서버로 보낼 임대 필드. 계산 결과를 그대로 스냅샷으로 동봉한다. */
export interface RentalItemPayload {
  rental_term_months:          number;
  rental_preset_id:            number;
  rental_margin_rate:          number;
  rental_manual_unit_krw:      number | null;
  rental_contract_cost_krw:    number;
  rental_short_term_months:    number;
  rental_short_term_surcharge: number;
  rental_normal_term_months:   number;
  rental_short_term_step_up:   number;
  rental_penalty_rate:         number;
  rental_penalty_adjust:       number;
  rental_costs: {
    label: string; kind: string; value: number;
    amount_krw: number; sort_order: number;
  }[];
}

/**
 * 라인 → 저장 페이로드.
 *
 * ★ 정책을 **프리셋에서 복사해** 함께 보낸다. 프리셋 id 만 보내면 나중에
 *   프리셋을 고친 순간 과거 견적의 근거가 같이 바뀐다. 마이그 179 가 금액까지
 *   저장하는 것과 같은 이유다.
 *
 * ★ 할증 곡선은 **네 값이 한 벌**이다 — 꼭짓점 T·할증률 S·정상 개월 L·가산율 U.
 *   앞의 둘만 보내던 시절은 계단이라 그것으로 충분했지만(마이그 178), 곡선이
 *   된 뒤로는 넷이 다 있어야 재현된다(마이그 187/188). 하나라도 빠뜨리면
 *   저장된 견적을 다시 열 때 다른 할증률이 나온다.
 */
export function toRentalItemPayload(r: RentalLineResult, row: RentalRowLike): RentalItemPayload {
  const manual = parseInt0(row.rentalManualUnitKrw);
  return {
    rental_term_months:          parseInt0(row.rentalTermMonths) ?? 0,
    rental_preset_id:            Number(r.preset.id),
    rental_margin_rate:          r.preset.target_margin_rate,
    rental_manual_unit_krw:      manual != null && manual > 0 ? manual : null,
    rental_contract_cost_krw:    r.pricing.contractCostKrw,
    rental_short_term_months:    r.preset.short_term_months,
    rental_short_term_surcharge: r.preset.short_term_surcharge,
    rental_normal_term_months:   r.preset.normal_term_months,
    rental_short_term_step_up:   r.preset.short_term_step_up,
    rental_penalty_rate:         r.preset.penalty_rate,
    rental_penalty_adjust:       r.preset.penalty_adjust,
    rental_costs: r.pricing.costs.map((c, i) => ({
      label:      c.label,
      kind:       c.kind,
      value:      c.value,
      amount_krw: c.amountKrw,
      sort_order: i,
    })),
  };
}
